import React, { useCallback, useState } from 'react';
import DisclosureViewerModal from './DisclosureViewerModal.jsx';
import { buildDisclosureDocumentList, disclosureViewNeedsPresignFetch } from './disclosureDocuments.js';

const emptyViewerState = {
  open: false,
  title: '',
  pdfUrl: '',
  loading: false,
  error: '',
};

async function fetchDisclosurePdfUrl(viewUrl) {
  const response = await fetch(viewUrl);
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload.error || `Failed to load document (${response.status})`);
  }
  if (!payload.viewUrl) {
    throw new Error('Document view URL was not returned by the API.');
  }
  return payload.viewUrl;
}

export default function DisclosuresSection({ apiBaseUrl }) {
  const disclosureDocs = buildDisclosureDocumentList(apiBaseUrl);
  const [viewer, setViewer] = useState(emptyViewerState);
  const [openingDocId, setOpeningDocId] = useState('');

  const closeViewer = useCallback(() => {
    setViewer(emptyViewerState);
  }, []);

  const openDisclosure = async (doc) => {
    if (!doc.viewUrl || openingDocId) {
      return;
    }
    setOpeningDocId(doc.id);
    setViewer({
      open: true,
      title: doc.title,
      pdfUrl: '',
      loading: true,
      error: '',
    });
    try {
      const pdfUrl = disclosureViewNeedsPresignFetch(doc.viewUrl)
        ? await fetchDisclosurePdfUrl(doc.viewUrl)
        : doc.viewUrl;
      setViewer({
        open: true,
        title: doc.title,
        pdfUrl,
        loading: false,
        error: '',
      });
    } catch (err) {
      setViewer({
        open: true,
        title: doc.title,
        pdfUrl: '',
        loading: false,
        error: err.message || 'Failed to open document.',
      });
    } finally {
      setOpeningDocId('');
    }
  };

  return (
    <>
      <h3 className="text-lg font-semibold text-gray-800 mb-3">Disclosures</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-8">
        {disclosureDocs.map((doc) => (
          <div key={doc.id} className="border rounded-xl p-4 flex items-center justify-between gap-3">
            <span className="text-sm font-medium text-gray-800">{doc.title}</span>
            {doc.viewUrl ? (
              <button
                type="button"
                disabled={openingDocId === doc.id}
                onClick={() => openDisclosure(doc)}
                className="px-3 py-2 rounded-md text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
              >
                {openingDocId === doc.id ? 'Opening...' : 'View'}
              </button>
            ) : (
              <span className="text-xs text-gray-500">URL not configured</span>
            )}
          </div>
        ))}
      </div>

      <DisclosureViewerModal
        open={viewer.open}
        title={viewer.title}
        pdfUrl={viewer.pdfUrl}
        loading={viewer.loading}
        error={viewer.error}
        onClose={closeViewer}
      />
    </>
  );
}
